import { useEffect, useMemo, useRef, useState } from 'react';
// @ts-expect-error player.js has no types
import playerjs from 'player.js';
import {
  PlayIcon,
  CheckIcon,
  ChevronDownIcon,
  ChevronRightIcon,
} from '@heroicons/react/24/outline';
import { api } from '@/lib/api';
import { cn, fmtSeconds } from '@/lib/utils';

interface PlayerProps {
  lessonId: string;
  iframeUrl: string;
  durationSeconds: number;
  initialPosition: number;
  initialFurthest: number;
  chapters: Array<{ time: number; title: string }>;
  ctaUrl?: string;
  ctaLabel?: string;
  startAt?: number;
  onCompletion?: () => void;
}

interface PendingEvent {
  lessonId: string;
  kind: 'play' | 'pause' | 'seek' | 'segment' | 'complete' | 'visibility_hidden';
  startSeconds: number;
  endSeconds: number;
  clientTs: number;
  sessionId: string;
}

// Each heartbeat costs one lesson_events insert AND one lesson_progress upsert,
// which together were the largest write source in the database. At 30s the
// watch-time totals are unchanged (segments still tile the playback) and the
// only thing we give up is resume-position accuracy after a hard crash -
// pause, end and tab-hide all still flush immediately. Completion is checked on
// every timeupdate tick rather than on the heartbeat, so drip unlocking is
// unaffected by this value.
const HEARTBEAT_SECONDS = 30;
const BUFFER_KEY = 'lesson_event_buffer_v1';
const FLUSH_MAX = 50;

function readBuffer(): PendingEvent[] {
  try {
    const raw = localStorage.getItem(BUFFER_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed.slice(0, 200) : [];
  } catch { return []; }
}
function writeBuffer(events: PendingEvent[]) {
  try { localStorage.setItem(BUFFER_KEY, JSON.stringify(events.slice(-200))); } catch {}
}

export function Player({
  lessonId, iframeUrl, durationSeconds, initialPosition, initialFurthest,
  chapters, ctaUrl, ctaLabel, startAt, onCompletion,
}: PlayerProps) {
  const iframeRef = useRef<HTMLIFrameElement>(null);
  const playerRef = useRef<{
    play: () => void;
    pause: () => void;
    setCurrentTime: (s: number) => void;
    on: (event: string, cb: (data?: unknown) => void) => void;
    off?: (event: string, cb?: (data?: unknown) => void) => void;
  } | null>(null);
  const sessionIdRef = useRef<string>(crypto.randomUUID());
  const lastBeatRef = useRef<number>(0);
  // Video position where the current, not-yet-reported watch span starts; -1
  // until playback reports a position. Segments are emitted as [anchor, now],
  // so they tile the playback exactly: a beat landing right after a resume
  // invents nothing, and the tail before a pause is not lost. Reset on seek,
  // because the span jumped over was never watched.
  const beatAnchorRef = useRef<number>(-1);
  const lastTimeRef = useRef<number>(0);
  const furthestRef = useRef<number>(initialFurthest);
  const completedFiredRef = useRef<boolean>(false);
  const seekedToStartRef = useRef<boolean>(false);
  // Mirror the props the init effect reads (once, or needs the latest of) into
  // refs, so a parent re-render — e.g. a course-progress refetch feeding a new
  // initialPosition/durationSeconds for the SAME lesson — does NOT tear down and
  // rebuild the playerjs.Player. Only iframeUrl/lessonId should drive re-init.
  const startAtRef = useRef(startAt);
  const initialPositionRef = useRef(initialPosition);
  const durationRef = useRef(durationSeconds);
  const onCompletionRef = useRef(onCompletion);
  startAtRef.current = startAt;
  initialPositionRef.current = initialPosition;
  durationRef.current = durationSeconds;
  onCompletionRef.current = onCompletion;
  const [currentTime, setCurrentTime] = useState(0);
  const [chaptersOpen, setChaptersOpen] = useState(false);

  const sortedChapters = useMemo(() => [...chapters].sort((a, b) => a.time - b.time), [chapters]);
  const currentChapterIdx = useMemo(() => {
    if (sortedChapters.length === 0) return -1;
    let idx = -1;
    for (let i = 0; i < sortedChapters.length; i++) {
      if (currentTime >= (sortedChapters[i]?.time ?? 0)) idx = i;
      else break;
    }
    return idx;
  }, [currentTime, sortedChapters]);

  const queueEvent = (e: Omit<PendingEvent, 'sessionId' | 'clientTs'>) => {
    const ev: PendingEvent = { ...e, clientTs: Math.floor(Date.now() / 1000), sessionId: sessionIdRef.current };
    const buf = readBuffer();
    buf.push(ev);
    writeBuffer(buf);
  };
  // Close the open watch span at `end` and re-anchor there. Silent when there
  // is nothing to report: no anchor yet, or the position has not moved.
  const emitSegment = (end: number) => {
    const start = beatAnchorRef.current;
    beatAnchorRef.current = end;
    if (start < 0 || end <= start) return;
    queueEvent({ lessonId, kind: 'segment', startSeconds: start, endSeconds: end });
  };
  const flushEvents = async () => {
    const buf = readBuffer();
    if (buf.length === 0) return;
    const chunk = buf.slice(0, FLUSH_MAX);
    try {
      await api.post('/api/course/events', { events: chunk });
      writeBuffer(buf.slice(chunk.length));
    } catch {}
  };
  const sendProgress = async (lastPosition: number) => {
    try {
      await api.post('/api/course/progress', {
        lessonId,
        furthestSeconds: furthestRef.current,
        lastPositionSeconds: lastPosition,
      });
    } catch {}
  };
  // Mark the lesson complete (fires once). We AWAIT the server write before
  // calling onCompletion so that the course refetch it triggers sees
  // completed_at already set — otherwise the refetch races the write and the
  // drip "next" button stays locked until a manual reload.
  const completeLesson = async () => {
    if (completedFiredRef.current) return;
    completedFiredRef.current = true;
    furthestRef.current = Math.max(furthestRef.current, durationRef.current || furthestRef.current);
    try {
      await api.post(`/api/course/lessons/${lessonId}/complete`);
    } catch {
      // Write failed — let a later tick retry rather than firing onCompletion
      // with an unwritten state (the optimistic unlock would then be reverted
      // by the reconcile refetch).
      completedFiredRef.current = false;
      return;
    }
    onCompletionRef.current?.();
  };

  useEffect(() => {
    const iframe = iframeRef.current;
    if (!iframe) return;

    // playerjs.Player wraps the iframe + speaks the Player.js postMessage
    // protocol (Embedly cross-vendor spec). Bunny iframe implements it.
    const player = new playerjs.Player(iframe);
    playerRef.current = player;

    player.on('ready', () => {
      // Initial seek (?t= URL param > last_position)
      const target = startAtRef.current !== undefined ? startAtRef.current : initialPositionRef.current;
      if (target > 0 && !seekedToStartRef.current) {
        seekedToStartRef.current = true;
        player.setCurrentTime(target);
      }
    });

    player.on('play', () => {
      // Anchor the span at the resume point and restart the beat clock, so the
      // first heartbeat after a resume reports what was actually watched.
      beatAnchorRef.current = lastTimeRef.current;
      lastBeatRef.current = Math.floor(Date.now() / 1000);
      queueEvent({ lessonId, kind: 'play', startSeconds: lastTimeRef.current, endSeconds: lastTimeRef.current });
    });

    player.on('pause', () => {
      emitSegment(lastTimeRef.current);
      queueEvent({ lessonId, kind: 'pause', startSeconds: lastTimeRef.current, endSeconds: lastTimeRef.current });
      void sendProgress(lastTimeRef.current);
      void flushEvents();
    });

    player.on('seeked', (data: unknown) => {
      const d = (data as { seconds?: number }) ?? {};
      const t = Number(d.seconds ?? lastTimeRef.current);
      // Close the span at the PRE-seek position first: what was watched up to
      // the jump counts, the stretch jumped over does not.
      emitSegment(lastTimeRef.current);
      beatAnchorRef.current = t;
      lastTimeRef.current = t;
      queueEvent({ lessonId, kind: 'seek', startSeconds: t, endSeconds: t });
    });

    player.on('ended', () => {
      emitSegment(lastTimeRef.current);
      queueEvent({ lessonId, kind: 'complete', startSeconds: lastTimeRef.current, endSeconds: lastTimeRef.current });
      furthestRef.current = Math.max(furthestRef.current, lastTimeRef.current);
      void sendProgress(lastTimeRef.current);
      void flushEvents();
      void completeLesson();
    });

    player.on('timeupdate', (data: unknown) => {
      const d = (data as { seconds?: number; duration?: number }) ?? {};
      const t = Number(d.seconds ?? 0);
      const dur = Number(d.duration ?? durationRef.current);
      lastTimeRef.current = t;
      setCurrentTime(t);
      if (t > furthestRef.current) furthestRef.current = t;

      // Complete as soon as the viewer reaches the last 10 seconds (or 75% as a
      // fallback when duration is reported but the tail is short). Checked on
      // EVERY tick, not only on the heartbeat, so the next lesson unlocks the
      // instant the threshold is crossed - no reload needed. Keep this 0.75 in
      // sync with the server-side completion threshold in course.ts.
      if (dur > 0 && t > 0 && (t >= dur - 10 || furthestRef.current >= dur * 0.75)) {
        void completeLesson();
      }

      const now = Math.floor(Date.now() / 1000);
      if (beatAnchorRef.current < 0) {
        // First position we have seen. Playback can start without us getting a
        // 'play' event (a restored autoplaying iframe), so anchor here and let
        // the first real beat land a full interval later.
        beatAnchorRef.current = t;
        lastBeatRef.current = now;
      } else if (now - lastBeatRef.current >= HEARTBEAT_SECONDS) {
        lastBeatRef.current = now;
        emitSegment(t);
        void sendProgress(t);
        void flushEvents();
      }
    });

    const onVisibility = () => {
      if (document.visibilityState === 'hidden') {
        emitSegment(lastTimeRef.current);
        queueEvent({
          lessonId,
          kind: 'visibility_hidden',
          startSeconds: lastTimeRef.current,
          endSeconds: lastTimeRef.current,
        });
        const buf = readBuffer();
        const chunk = buf.slice(0, FLUSH_MAX);
        if (chunk.length > 0) {
          const blob = new Blob([JSON.stringify({ events: chunk })], { type: 'application/json' });
          if (navigator.sendBeacon('/api/course/events', blob)) {
            writeBuffer(buf.slice(chunk.length));
          }
        }
      }
    };
    document.addEventListener('visibilitychange', onVisibility);

    return () => {
      document.removeEventListener('visibilitychange', onVisibility);
      try { player.off?.('play'); player.off?.('pause'); player.off?.('seeked'); player.off?.('ended'); player.off?.('timeupdate'); } catch {}
      playerRef.current = null;
    };
  }, [iframeUrl, lessonId]);

  const seekTo = (sec: number) => {
    playerRef.current?.setCurrentTime(sec);
    playerRef.current?.play();
  };

  return (
    <div className="space-y-4">
      <div className="overflow-hidden rounded-lg bg-black shadow">
        <iframe
          ref={iframeRef}
          src={iframeUrl}
          allow="accelerometer; gyroscope; autoplay; encrypted-media; picture-in-picture;"
          allowFullScreen
          className="aspect-video w-full"
          title="Lektionsvideo"
        />
      </div>

      {ctaUrl?.trim() && (
        <a
          href={ctaUrl.trim()}
          target="_blank"
          rel="noopener noreferrer"
          className="flex w-full items-center justify-center rounded-lg bg-primary px-6 py-3.5 text-center text-base font-semibold text-primary-foreground shadow transition-colors hover:bg-primary/90"
        >
          {ctaLabel?.trim() || 'Mehr erfahren'}
        </a>
      )}

      {sortedChapters.length > 0 && (
        <section className="overflow-hidden rounded-lg border bg-card text-card-foreground">
          <button
            type="button"
            onClick={() => setChaptersOpen((v) => !v)}
            className="flex w-full items-center gap-2 px-4 py-2.5 text-left text-sm font-medium text-foreground hover:bg-muted/50"
          >
            {chaptersOpen ? (
              <ChevronDownIcon className="h-4 w-4 shrink-0 text-muted-foreground" />
            ) : (
              <ChevronRightIcon className="h-4 w-4 shrink-0 text-muted-foreground" />
            )}
            <div className="flex min-w-0 flex-1 items-baseline gap-2">
              <span className="shrink-0">Kapitel</span>
              {currentChapterIdx >= 0 && sortedChapters[currentChapterIdx] && (
                <span className="min-w-0 flex-1 truncate text-xs font-normal text-muted-foreground">
                  jetzt:{' '}
                  <span className="text-foreground">
                    {sortedChapters[currentChapterIdx]!.title}
                  </span>
                </span>
              )}
            </div>
            <span className="shrink-0 text-xs font-normal text-muted-foreground">
              {sortedChapters.length}
            </span>
          </button>
          {chaptersOpen && (
            <ul className="divide-y border-t">
              {sortedChapters.map((ch, idx) => {
                const isActive = idx === currentChapterIdx;
                const isPast = idx < currentChapterIdx;
                return (
                  <li key={idx}>
                    <button
                      type="button"
                      onClick={() => seekTo(ch.time)}
                      className={cn(
                        'flex w-full items-start gap-3 px-4 py-3 text-left text-sm transition-colors',
                        isActive ? 'bg-muted' : 'hover:bg-muted/50',
                      )}
                    >
                      {isPast ? (
                        <CheckIcon className="mt-0.5 h-4 w-4 shrink-0 text-primary-strong" />
                      ) : (
                        <PlayIcon className="mt-0.5 h-3.5 w-3.5 shrink-0 text-muted-foreground" />
                      )}
                      <span className="flex-1 leading-snug break-words text-foreground">
                        {ch.title}
                      </span>
                      <span className="shrink-0 tabular-nums text-muted-foreground">
                        {fmtSeconds(ch.time)}
                      </span>
                    </button>
                  </li>
                );
              })}
            </ul>
          )}
        </section>
      )}
    </div>
  );
}
